import type {
  ExteriorMaterial,
  Foundation,
  PropertyRow,
  RoofMaterial,
} from '@/db/types';

/**
 * Multi-value attribute helpers — siding, roof, and foundation can
 * each hold multiple materials in real homes (brick front + vinyl
 * sides, main asphalt + flat porch roof, split-level basement + slab).
 *
 * Storage shape:
 *   - Single-value column (roof_material, exterior_material,
 *     foundation) stays in the schema for backward compatibility.
 *     Holds the first/primary value when multi is set.
 *   - JSON-array column (roof_materials_json, …) is the canonical
 *     multi-select source of truth.
 *
 * Read pattern: prefer the JSON array; if null, fall back to a
 * one-element array containing the single-value column (or empty if
 * neither is set).
 *
 * Write pattern: when we have an array, store JSON.stringify(arr)
 * AND set the single-value column to arr[0] so legacy read paths
 * keep working.
 */

function parseJsonArray<T extends string>(raw: string | null): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is T => typeof x === 'string');
  } catch {
    return [];
  }
}

export function getRoofMaterials(p: PropertyRow): RoofMaterial[] {
  const arr = parseJsonArray<RoofMaterial>(p.roof_materials_json);
  if (arr.length > 0) return arr;
  if (p.roof_material && p.roof_material !== 'unsure') return [p.roof_material];
  return [];
}

export function getExteriorMaterials(p: PropertyRow): ExteriorMaterial[] {
  const arr = parseJsonArray<ExteriorMaterial>(p.exterior_materials_json);
  if (arr.length > 0) return arr;
  if (
    p.exterior_material &&
    p.exterior_material !== 'other'
  ) {
    return [p.exterior_material];
  }
  return [];
}

export function getFoundations(p: PropertyRow): Foundation[] {
  const arr = parseJsonArray<Foundation>(p.foundations_json);
  if (arr.length > 0) return arr;
  if (p.foundation && p.foundation !== 'unsure') return [p.foundation];
  return [];
}

/** Stringify an array for write paths. Returns null when the array
 *  is empty so the DB column reads as "no choice yet" rather than
 *  `[]`. */
export function serializeMaterialArray<T extends string>(arr: T[]): string | null {
  if (!arr || arr.length === 0) return null;
  return JSON.stringify(arr);
}
